import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Autocomplete,
  AutocompleteItem,
  Spinner,
} from "@heroui/react";
import { Textarea } from "components/ui/textarea";
import { useState, useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { RenovationProject } from "shared/types/renovation";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { formatCurrency } from "shared/utils/currency";

import { Info, SplitHorizontal } from "@phosphor-icons/react";
import SplitInvoiceModal, { SplitInvoiceLine } from "./SplitInvoiceModal";
import { VatWarningBadge } from "./VatWarningBadge";
import {
  resolveInvoiceVat,
  VatSource,
  VAT_UNKNOWN,
} from "shared/utils/invoiceVat";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  buildingId: string;
  onSuccess: () => void;
  availableProjects: RenovationProject[];
  renovationInvoices: Array<{
    partner_id: [number, string];
    date: string;
    name: string;
    move_line_id: [number, string];
    /** Excl btw — Odoo boekt de btw op aparte regels. */
    amount: number;
    amount_untaxed: number;
    general_account_id: [number, string];
    account_id: [number, string];
    move_name: string;
    move_type?: string;
    // BTW-uitsplitsing uit Odoo, zie shared/utils/invoiceVat.
    amount_excl_vat?: number;
    amount_vat?: number;
    amount_incl_vat?: number;
    vat_percentage?: number | null;
    vat_label?: string | null;
    vat_source?: VatSource | null;
  }>;
}

interface InvoiceAssignment {
  moveLineId: number;
  projectId: number;
  partId: number;
  amount: number;
  amount_untaxed: number;
  description?: string;
  remarks?: string;
  splitLines?: SplitInvoiceLine[];
}

export const RenovationInvoiceImportModal = ({
  isOpen,
  onClose,
  buildingId,
  onSuccess,
  availableProjects,
  renovationInvoices,
}: Props) => {
  const { session } = useSession();
  const [assignments, setAssignments] = useState<
    Record<number, InvoiceAssignment>
  >({});
  const [selectedProjects, setSelectedProjects] = useState<
    Record<number, number>
  >({});
  const [supplierAssignments, setSupplierAssignments] = useState<
    Record<number, number>
  >({});
  const [remarks, setRemarks] = useState<Record<number, string>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInvalidInvoices, setShowInvalidInvoices] = useState(false);

  // Memoized datasets to avoid repeated heavy computations during render/handlers
  const suppliersList = useMemo(() => {
    const supplierMap = new Map<
      number,
      {
        id: number;
        name: string;
        odooId?: string;
        projectParts: Array<{
          projectId: number;
          partId: number;
          projectTitle: string;
          partTitle: string;
          supplierFolderId?: string;
        }>;
      }
    >();

    availableProjects.forEach((project) => {
      (project.parts || []).forEach((part: any) => {
        (part.supplierAssignments || []).forEach((sa: any) => {
          const supplier = sa.supplier;
          if (!supplier) return;

          if (!supplierMap.has(supplier.id)) {
            supplierMap.set(supplier.id, {
              id: supplier.id,
              name: supplier.name,
              odooId: supplier.odoo_id?.toString(),
              projectParts: [],
            });
          }

          supplierMap.get(supplier.id)?.projectParts.push({
            projectId: project.id,
            partId: part.id,
            projectTitle: project.title,
            partTitle: part.title,
            supplierFolderId: sa.supplierFolderId,
          });
        });
      });
    });

    return Array.from(supplierMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [availableProjects]);

  const suppliersMapById = useMemo(() => {
    const map = new Map();
    suppliersList.forEach((supplier) => map.set(supplier.id, supplier));
    return map;
  }, [suppliersList]);

  const partsByProjectSupplier = useMemo(() => {
    const map = new Map<string, any[]>();
    availableProjects.forEach((project) => {
      (project.parts || []).forEach((part: any) => {
        (part.supplierAssignments || []).forEach((sa: any) => {
          const key = `${project.id}:${sa.supplier.id}`;
          if (!map.has(key)) map.set(key, []);
          map.get(key)?.push(part);
        });
      });
    });
    return map;
  }, [availableProjects]);

  const invoiceByMoveLineId = useMemo(() => {
    const map = new Map();
    renovationInvoices.forEach((invoice) =>
      map.set(invoice.move_line_id[0], invoice),
    );
    return map;
  }, [renovationInvoices]);

  // Helper function to properly format invoice amounts (similar to SplitInvoiceModal)
  const formatInvoiceAmounts = (invoice: any) => {
    // Use absolute values for the UI so users work with positive numbers
    const totalInvoiceAmount = Math.abs(invoice.amount || 0);
    const totalUntaxed = Math.abs(invoice.amount_untaxed || totalInvoiceAmount);

    // Round to 2 decimal places to avoid floating point issues
    return {
      amount: Math.round(totalInvoiceAmount * 100) / 100,
      amount_untaxed: Math.round(totalUntaxed * 100) / 100,
    };
  };

  const findMatchingSupplierAndAssignments = (invoice: any) => {
    // Guard against missing or malformed partner_id
    if (
      !invoice?.partner_id ||
      !Array.isArray(invoice.partner_id) ||
      !invoice.partner_id[0]
    ) {
      console.warn("Invoice missing partner_id:", invoice);
      return [];
    }

    const supplierOdooId = invoice.partner_id[0].toString();

    const matches = availableProjects.flatMap((project) =>
      project.parts.flatMap(
        (part) =>
          part.supplierAssignments
            ?.filter((sa) => sa.supplier.odoo_id?.toString() === supplierOdooId)
            .map((sa) => ({
              supplierId: sa.supplier.id,
              projectId: project.id,
              partId: part.id,
              supplier: sa.supplier,
            })) || [],
      ),
    );

    return matches;
  };

  // Track whether we've already initialized for this modal open session.
  // This prevents background data refetches (e.g. refetchOnWindowFocus) from
  // wiping out user selections while the modal is open.
  const isInitializedRef = useRef(false);

  // Move line IDs whose supplier was filled in AUTOMATICALLY (Odoo partner
  // matched exactly one YallyHub supplier) and that the user has not touched.
  // An auto-matched supplier is a convenience, NOT a statement that the user
  // wants to import that invoice — so these rows must never block the import
  // of the rows the user did assign. See handleImport.
  const autoSelectedSupplierIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setAssignments({});
      setSelectedProjects({});
      setSupplierAssignments({});
      setRemarks({});
      setIsLoading(true);
      setShowInvalidInvoices(false);
      isInitializedRef.current = false;
      autoSelectedSupplierIdsRef.current = new Set();
      return;
    }

    // Only initialize once per modal open — skip if already initialized
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Batch all state updates into a single operation
    const autoSelected = new Set<number>();
    const newState = renovationInvoices.reduce(
      (acc, invoice) => {
        const moveLineId = invoice.move_line_id[0];
        const matches = findMatchingSupplierAndAssignments(invoice);
        const formattedAmounts = formatInvoiceAmounts(invoice);

        // The supplier is unambiguous as soon as the Odoo partner matches a
        // single YallyHub supplier — even if that supplier is linked to several
        // project parts. Auto-select the supplier in that case; only pre-select
        // the project + part when there is exactly ONE possible assignment.
        const uniqueSupplierIds = Array.from(
          new Set(matches.map((m) => m.supplierId)),
        );
        const autoSupplierId =
          uniqueSupplierIds.length === 1 ? uniqueSupplierIds[0] : undefined;

        acc.assignments[moveLineId] = {
          moveLineId,
          projectId: matches.length === 1 ? matches[0].projectId : 0,
          partId: matches.length === 1 ? matches[0].partId : 0,
          amount: formattedAmounts.amount,
          amount_untaxed: formattedAmounts.amount_untaxed,
          description: invoice.name,
          remarks: "",
        };

        if (autoSupplierId !== undefined) {
          acc.supplierAssignments[moveLineId] = autoSupplierId;
          // Remember that WE filled this in, so an auto-matched-but-unassigned
          // row does not abort the import of the rows the user did assign.
          autoSelected.add(moveLineId);
        }
        if (matches.length === 1) {
          acc.selectedProjects[moveLineId] = matches[0].projectId;
        }

        acc.remarks[moveLineId] = "";

        return acc;
      },
      {
        assignments: {} as Record<number, InvoiceAssignment>,
        selectedProjects: {} as Record<number, number>,
        supplierAssignments: {} as Record<number, number>,
        remarks: {} as Record<number, string>,
      },
    );

    // Update all state at once
    autoSelectedSupplierIdsRef.current = autoSelected;
    setAssignments(newState.assignments);
    setSelectedProjects(newState.selectedProjects);
    setSupplierAssignments(newState.supplierAssignments);
    setRemarks(newState.remarks);
    setIsLoading(false);
  }, [isOpen, renovationInvoices, availableProjects]);

  const getAvailablePartsForInvoice = (invoice: any, projectId: number) => {
    // Get the current supplier ID from our assignments
    const currentSupplierId = supplierAssignments[invoice.move_line_id[0]];

    if (!currentSupplierId) {
      console.log("No supplier assigned for this invoice");
      return [];
    }

    const key = `${projectId}:${currentSupplierId}`;
    const filteredParts = partsByProjectSupplier.get(key) || [];

    console.log("Filtered parts:", {
      projectId,
      currentSupplierId,
      partsFound: filteredParts.length,
    });

    return filteredParts;
  };

  const handleClose = () => {
    setAssignments({});
    setSelectedProjects({});
    onClose();
  };

  /**
   * Move line IDs the user DELIBERATELY picked a supplier for. Rows where the
   * supplier was auto-filled from the Odoo partner are excluded: such a
   * supplier can be linked to parts in several projects, leaving project + part
   * empty, and treating those as "selected" would block the import of every
   * other invoice (button disabled + guard in handleImport).
   */
  const getUserSelectedMoveLineIds = () =>
    Object.keys(supplierAssignments)
      .map(Number)
      .filter((moveLineId) => !autoSelectedSupplierIdsRef.current.has(moveLineId));

  const allSelectedInvoicesAssigned = () => {
    return getUserSelectedMoveLineIds().every((moveLineId) => {
      const assignment = assignments[moveLineId];

      // Invoice is assigned if it has a direct part assignment OR valid split lines
      return (
        (assignment?.projectId && assignment?.partId) ||
        (assignment?.splitLines &&
          assignment.splitLines.length > 0 &&
          assignment.splitLines.every(
            (line) =>
              line.projectId > 0 &&
              line.partId > 0 &&
              line.description &&
              line.description.trim() !== "",
          ))
      );
    });
  };

  const getAssignedInvoiceCount = () => {
    return Object.values(assignments).filter(
      (assignment) =>
        (assignment.projectId && assignment.partId) ||
        (assignment.splitLines &&
          assignment.splitLines.length > 0 &&
          assignment.splitLines.every(
            (line) =>
              line.projectId > 0 &&
              line.partId > 0 &&
              line.description &&
              line.description.trim() !== "",
          )),
    ).length;
  };

  const getUniqueSuppliers = () => {
    return suppliersList;
  };

  const handleRemarksChange = (moveLineId: number, value: string) => {
    setRemarks((prev) => ({
      ...prev,
      [moveLineId]: value,
    }));

    // Also update the assignment
    setAssignments((prev) => {
      if (prev[moveLineId]) {
        return {
          ...prev,
          [moveLineId]: {
            ...prev[moveLineId],
            remarks: value,
          },
        };
      }
      return prev;
    });
  };

  const handleOpenSplitModal = (invoice: any) => {
    const moveLineId = invoice.move_line_id[0];

    // Project + part are now chosen inside the split modal, so we only need a
    // supplier here (the available parts depend on it).
    if (!supplierAssignments[moveLineId]) {
      toast.warn("Selecteer eerst een leverancier");
      return;
    }

    // Set the current invoice for the split modal
    setCurrentInvoice(invoice);

    // Open the split modal
    setSplitModalOpen(true);
  };

  const handleSaveSplit = (
    moveLineId: number,
    splitLines: SplitInvoiceLine[],
  ) => {
    // Update the assignment to include the split lines
    setAssignments((prev: any) => {
      const currentAssignment = prev[moveLineId];
      if (!currentAssignment) return prev;

      return {
        ...prev,
        [moveLineId]: {
          ...currentAssignment,
          splitLines: splitLines,
          // Clear the direct partId since we're using split lines
          partId: undefined,
        },
      };
    });

    toast.success("Factuurverdeling opgeslagen");
  };

  const handleImport = async () => {
    try {
      const validAssignments = Object.values(assignments).filter(
        (assignment) =>
          (assignment.projectId && assignment.partId) ||
          (assignment.splitLines &&
            assignment.splitLines.length > 0 &&
            assignment.splitLines.every(
              (line) => line.projectId > 0 && line.partId > 0,
            )),
      );

      if (validAssignments.length === 0) {
        toast.error("Wijs minstens één factuur toe aan een projectonderdeel");
        return;
      }

      // Only rows the user deliberately picked a supplier for must be fully
      // assigned — see getUserSelectedMoveLineIds.
      const unassignedSelectedIds = getUserSelectedMoveLineIds().filter((moveLineId) => {
        const assignment = assignments[moveLineId];
        return !(
          (assignment?.projectId && assignment?.partId) ||
          (assignment?.splitLines &&
            assignment.splitLines.length > 0 &&
            assignment.splitLines.every((line) => line.partId > 0))
        );
      });
      if (unassignedSelectedIds.length > 0) {
        console.warn("Selected invoices without a project part assignment:", {
          buildingId,
          unassignedSelectedIds,
        });
        toast.error(
          "Alle geselecteerde facturen moeten aan een projectonderdeel toegewezen zijn",
        );
        return;
      }

      setIsImporting(true);

      const promises = validAssignments.map(async (assignment) => {
        const currentSupplierId = supplierAssignments[assignment.moveLineId];

        if (!currentSupplierId) {
          throw new Error(
            `Geen leverancier geselecteerd voor factuurregel ${assignment.moveLineId}`,
          );
        }

        // Find the unique supplier with associated project parts
        const selectedSupplier = suppliersMapById.get(currentSupplierId);

        // Check if this invoice has split lines
        const hasSplitLines =
          assignment.splitLines && assignment.splitLines.length > 0;

        // The top-level projectId stays 0 whenever the supplier spans multiple
        // projects — project + part are then chosen per split line. Derive it
        // from the first split line so the backend always receives a real
        // project (it uses this one as the document's primary project).
        const primaryProjectId = hasSplitLines
          ? assignment.splitLines!.find((line) => line.projectId > 0)
              ?.projectId ?? assignment.projectId
          : assignment.projectId;

        // Find the matching project part for this supplier
        let supplierFolderId;
        if (hasSplitLines) {
          // For split invoices, derive the supplier folder from the first split
          // line's OWN project/part (lines may now span multiple projects).
          const firstSplitLine = assignment.splitLines![0];
          const matchingPart = selectedSupplier?.projectParts.find(
            (part: any) =>
              part.projectId ===
                (firstSplitLine.projectId ?? assignment.projectId) &&
              part.partId === firstSplitLine.partId,
          );
          supplierFolderId = matchingPart?.supplierFolderId;
        } else {
          // For single invoices, use the assignment's partId
          const matchingPart = selectedSupplier?.projectParts.find(
            (part: any) =>
              part.projectId === assignment.projectId &&
              part.partId === assignment.partId,
          );
          supplierFolderId = matchingPart?.supplierFolderId;
        }

        // Get the invoice details that match this assignment
        const invoiceData = invoiceByMoveLineId.get(assignment.moveLineId);

        if (!invoiceData) {
          throw new Error(
            `Factuurgegevens niet gevonden voor factuurregel ${assignment.moveLineId}`,
          );
        }

        // Prepare the partializedInvoiceLines based on split or single.
        // Convert amounts back to negative values for backend (since invoice
        // amounts are typically negative in Odoo).
        //
        // BTW komt uit Odoo (zie shared/utils/invoiceVat): `amount` op de feed is
        // EXCL btw, want Odoo boekt de btw op aparte boekingsregels. Eerder werd
        // hier `amount_untaxed` als incl en `amount` als excl weggeschreven — de
        // twee kolommen stonden dus omgewisseld — en werden vatAmount/
        // vatPercentage altijd op 0 gezet.
        const invoiceVat = resolveInvoiceVat(invoiceData);

        // Bij een splitsing wordt de btw pro rata verdeeld over de delen, op
        // basis van hun aandeel in het excl-btw bedrag. Zo blijft de som van de
        // regels gelijk aan de factuur.
        const splitVatFor = (lineExclVat: number) => {
          const share =
            invoiceVat.exclVat > 0
              ? Math.abs(lineExclVat) / invoiceVat.exclVat
              : 0;
          const vatAmount = Math.round(invoiceVat.vatAmount * share * 100) / 100;
          return {
            vatAmount,
            totalInclVat: Math.round((Math.abs(lineExclVat) + vatAmount) * 100) / 100,
          };
        };

        const partializedInvoiceLines = hasSplitLines
          ? assignment.splitLines!.map((splitLine) => {
              const split = splitVatFor(splitLine.amount);
              return {
                partId: splitLine.partId,
                // Per-line project so the invoice can span multiple projects.
                projectId: splitLine.projectId,
                totalExclVat: -Math.abs(splitLine.amount),
                totalInclVat: -split.totalInclVat,
                vatAmount: -split.vatAmount,
                vatPercentage: invoiceVat.isUnknown
                  ? null
                  : invoiceData.vat_percentage ?? null,
                vatSource: invoiceData.vat_source ?? null,
                customDescription: splitLine.description || "",
              };
            })
          : [
              {
                partId: assignment.partId,
                projectId: assignment.projectId,
                totalExclVat: -Math.abs(assignment.amount),
                totalInclVat: -invoiceVat.inclVat,
                vatAmount: -invoiceVat.vatAmount,
                vatPercentage: invoiceVat.isUnknown
                  ? null
                  : invoiceData.vat_percentage ?? null,
                vatSource: invoiceData.vat_source ?? null,
                customDescription: assignment.description || "",
              },
            ];

        const requestBody = {
          buildingId,
          projectId: primaryProjectId,
          moveLineId: assignment.moveLineId,
          // Odoo move_type so the backend flags vendor credit notes
          // ('in_refund') and nets them in the kostenplaats totals.
          moveType: invoiceData.move_type,
          partializedInvoiceLines,
          selectedSupplierId: currentSupplierId,
          selectedSupplierFolderId: supplierFolderId,
          // Include remarks for the document entity
          remarks:
            assignment.remarks ||
            remarks[assignment.moveLineId] ||
            invoiceData.name,
          // Include invoice reference data to help with debugging/tracing
          invoiceReference: {
            invoiceNumber: invoiceData.move_name,
            invoiceDate: invoiceData.date,
            supplierName: invoiceData.partner_id[1],
            supplierOdooId: invoiceData.partner_id[0],
          },
        };

        if (!requestBody.selectedSupplierFolderId) {
          // The chosen part carries no SharePoint supplier folder, so the
          // document would have nowhere to live. Surface it per invoice with
          // enough context to fix the link in the project part.
          const context = {
            moveLineId: assignment.moveLineId,
            buildingId,
            projectId: primaryProjectId,
            partId: assignment.partId,
            hasSplitLines,
            splitLines: assignment.splitLines,
            selectedSupplier: selectedSupplier
              ? {
                  id: selectedSupplier.id,
                  name: selectedSupplier.name,
                  projectParts: selectedSupplier.projectParts.map((p: any) => ({
                    projectId: p.projectId,
                    partId: p.partId,
                    supplierFolderId: p.supplierFolderId,
                  })),
                }
              : null,
          };
          console.error("Missing supplierFolderId for invoice:", context);
          Sentry.captureMessage(
            "Renovation invoice import: missing supplier folder",
            {
              level: "error",
              tags: { component: "RenovationInvoiceImportModal" },
              extra: context,
            },
          );
          throw new Error(
            `Geen leveranciersmap gevonden voor factuur ${invoiceData.move_name} — gelieve de leverancier opnieuw te selecteren`,
          );
        }

        console.log("Import request:", JSON.stringify(requestBody, null, 2));

        try {
          const response = await fetchWithAuth(
            `${API_URL}/renovation/document/import-invoice`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestBody),
            },
            session,
          );

          if (!response.ok) {
            const body = await response.text().catch(() => response.statusText);
            throw new Error(
              `Import van factuur ${invoiceData.move_name} mislukt (${response.status}): ${body}`,
            );
          }

          const result = await response.json();
          console.log("Import response:", result);

          // Validate the response has the expected document lines and part connections
          if (result.lines) {
            // For split lines, check if all parts are connected
            if (hasSplitLines) {
              const partIds = assignment.splitLines!.map((sl) => sl.partId);
              const foundPartConnections = result.lines
                .filter((l: any) => l.part && partIds.includes(l.part.id))
                .map((l: any) => l.part.id);

              if (foundPartConnections.length !== partIds.length) {
                console.warn(
                  "Warning: Not all document lines are connected to the expected parts:",
                  {
                    documentId: result.id,
                    expectedPartIds: partIds,
                    foundPartIds: foundPartConnections,
                    lines: result.lines.map((l: any) => ({
                      lineId: l.id,
                      partId: l.part?.id,
                    })),
                  },
                );
              }
            } else {
              // Original single part check
              const foundPartConnection = result.lines.some(
                (l: any) => l.part && l.part.id === assignment.partId,
              );
              if (!foundPartConnection) {
                console.warn(
                  "Warning: Imported document does not have a line connected to the selected part:",
                  {
                    documentId: result.id,
                    expectedPartId: assignment.partId,
                    lines: result.lines.map((l: any) => ({
                      lineId: l.id,
                      partId: l.part?.id,
                    })),
                  },
                );
              }
            }
          }

          return result;
        } catch (error) {
          console.error("Error importing invoice:", error);
          Sentry.captureException(error, {
            tags: { component: "RenovationInvoiceImportModal" },
            extra: {
              buildingId,
              moveLineId: assignment.moveLineId,
              projectId: primaryProjectId,
              invoiceNumber: invoiceData.move_name,
              hasSplitLines,
            },
          });
          throw error;
        }
      });

      // allSettled (not all) so one bad invoice cannot hide the outcome of the
      // others: partial success is reported as partial, never as success.
      const results = await Promise.allSettled(promises);
      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const importedCount = results.length - failures.length;

      if (failures.length > 0) {
        console.error("Renovation invoice import finished with failures:", {
          buildingId,
          total: results.length,
          importedCount,
          reasons: failures.map((f) => f.reason?.message ?? String(f.reason)),
        });
        Sentry.captureException(
          failures[0].reason instanceof Error
            ? failures[0].reason
            : new Error(String(failures[0].reason)),
          {
            tags: { component: "RenovationInvoiceImportModal" },
            extra: {
              buildingId,
              total: results.length,
              importedCount,
              failedCount: failures.length,
              reasons: failures.map(
                (f) => f.reason?.message ?? String(f.reason),
              ),
            },
          },
        );

        if (importedCount === 0) {
          toast.error(
            failures[0].reason?.message ?? "Fout bij importeren facturen",
          );
          return;
        }

        toast.warn(
          `${importedCount} van ${results.length} facturen geïmporteerd — ${failures.length} mislukt`,
        );
      } else {
        toast.success(
          importedCount === 1
            ? "Factuur succesvol geïmporteerd"
            : `${importedCount} facturen succesvol geïmporteerd`,
        );
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error importing invoices:", error);
      Sentry.captureException(error, {
        tags: { component: "RenovationInvoiceImportModal" },
        extra: {
          buildingId,
          assignmentCount: Object.keys(assignments).length,
        },
      });
      toast.error("Fout bij importeren facturen");
    } finally {
      setIsImporting(false);
    }
  };

  const handleSupplierSelect = (moveLineId: number, resource: any) => {
    const supplierId = Number(resource.resourceId);
    console.log("handleSupplierSelect:", {
      moveLineId,
      resourceId: resource.resourceId,
      supplierId,
      previousAssignment: supplierAssignments[moveLineId],
    });

    // Ensure we have a valid supplier ID
    if (!supplierId || isNaN(supplierId)) {
      console.error("Invalid supplier ID:", resource.resourceId);
      Sentry.captureMessage("Invalid supplier ID in invoice import", {
        level: "warning",
        tags: { component: "RenovationInvoiceImportModal" },
        extra: { moveLineId, resourceId: resource.resourceId, buildingId },
      });
      return;
    }

    // The user picked this supplier deliberately, so from now on this invoice
    // MUST be fully assigned before the import can run.
    autoSelectedSupplierIdsRef.current.delete(moveLineId);

    setSupplierAssignments((prev) => ({
      ...prev,
      [moveLineId]: supplierId,
    }));

    // Get the selected supplier from memoized data
    const selectedSupplier = suppliersMapById.get(supplierId);

    if (!selectedSupplier) {
      console.error("Selected supplier not found in unique suppliers list");
      return;
    }

    // Find all project parts where this supplier is assigned
    const matchingParts = selectedSupplier.projectParts;

    console.log("Found matching parts:", {
      supplierId,
      supplierName: selectedSupplier.name,
      matchingParts: matchingParts,
    });

    if (matchingParts.length === 1) {
      // If only one match, auto-select both project and part
      const match = matchingParts[0];
      console.log("Auto-selecting single match:", match);

      // Get the invoice data and format amounts
      const invoice = invoiceByMoveLineId.get(moveLineId);
      const formattedAmounts = formatInvoiceAmounts(invoice);

      // First set the project
      setSelectedProjects((prev) => ({
        ...prev,
        [moveLineId]: match.projectId,
      }));

      // Then set the assignment with both project and part
      setAssignments((prev) => ({
        ...prev,
        [moveLineId]: {
          moveLineId,
          projectId: match.projectId,
          partId: match.partId,
          amount: formattedAmounts.amount,
          amount_untaxed: formattedAmounts.amount_untaxed,
          description: invoice?.name || "",
          remarks: "",
        },
      }));
    } else if (matchingParts.length > 1) {
      // If multiple matches in the same project, select the project but leave part selection to user
      const uniqueProjects = Array.from(
        new Set(matchingParts.map((m: any) => m.projectId)),
      );
      if (uniqueProjects.length === 1) {
        console.log(
          "Auto-selecting project for multiple parts in same project:",
          uniqueProjects[0],
        );

        // Get the invoice data and format amounts
        const invoice = invoiceByMoveLineId.get(moveLineId);
        const formattedAmounts = formatInvoiceAmounts(invoice);

        setSelectedProjects((prev) => ({
          ...prev,
          [moveLineId]: uniqueProjects[0] as number,
        }));

        setAssignments((prev) => ({
          ...prev,
          [moveLineId]: {
            moveLineId,
            projectId: uniqueProjects[0] as number,
            partId: 0, // Reset part selection
            amount: formattedAmounts.amount,
            amount_untaxed: formattedAmounts.amount_untaxed,
            description: invoice?.name || "",
            remarks: "",
          },
        }));
      } else {
        // Multiple projects, reset both selections
        console.log("Multiple projects found, resetting selections");
        setSelectedProjects((prev) => ({ ...prev, [moveLineId]: 0 }));
        setAssignments((prev) => ({
          ...prev,
          [moveLineId]: {
            ...prev[moveLineId],
            projectId: 0,
            partId: 0,
            // Clear any split from a previously chosen supplier.
            splitLines: undefined,
          },
        }));
      }
    } else {
      // No matches, reset selections
      console.log("No matches found, resetting selections");
      setSelectedProjects((prev) => ({ ...prev, [moveLineId]: 0 }));
      setAssignments((prev) => ({
        ...prev,
        [moveLineId]: {
          ...prev[moveLineId],
          projectId: 0,
          partId: 0,
        },
      }));
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="4xl"
        scrollBehavior="inside"
        className="max-h-[80vh] overflow-y-auto"
      >
        <ModalContent className="w-full max-w-[95vw] md:max-w-[90vw] lg:min-w-[70vw]">
          {isImporting && (
            <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-black/50">
              <div className="flex flex-col items-center gap-2 rounded-lg bg-white p-4">
                <div className="border-moss-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
                <div className="text-sm">Facturen importeren...</div>
              </div>
            </div>
          )}
          <ModalHeader className="flex flex-col gap-2">
            <div>Importeer facturen</div>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Info size={16} className="text-neutral-400" />
              Je kan alleen facturen van leveranciers importeren die gekoppeld
              zijn aan een projectonderdeel
            </div>
          </ModalHeader>
          <ModalBody>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Spinner
                  size="lg"
                  color="primary"
                  label="Loading invoices..."
                  classNames={{
                    label: "text-primary-500 text-lg mt-2",
                  }}
                />
              </div>
            ) : (
              (() => {
                // Order by invoice date (oldest first); undated rows go last.
                const byDate = (
                  a: (typeof renovationInvoices)[number],
                  b: (typeof renovationInvoices)[number],
                ) => {
                  const ta = a?.date ? new Date(a.date).getTime() : NaN;
                  const tb = b?.date ? new Date(b.date).getTime() : NaN;
                  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
                  if (Number.isNaN(ta)) return 1;
                  if (Number.isNaN(tb)) return -1;
                  return ta - tb;
                };

                // Separate valid and invalid invoices, each ordered by date.
                const invalidInvoices = renovationInvoices
                  .filter(
                    (inv) => !inv?.move_line_id?.[0] || !inv?.partner_id?.[0],
                  )
                  .sort(byDate);
                const validInvoices = renovationInvoices
                  .filter(
                    (inv) => inv?.move_line_id?.[0] && inv?.partner_id?.[0],
                  )
                  .sort(byDate);

                return (
                  <>
                    {/* Show warning banner for invalid invoices */}
                    {invalidInvoices.length > 0 && (
                      <div className="bg-brick-secondary-fine border-brick-primary mb-4 rounded-md border p-4">
                        <div className="flex items-start gap-3">
                          <Info
                            size={24}
                            className="text-brick-primary mt-0.5 shrink-0"
                          />
                          <div className="flex-1">
                            <button
                              onClick={() =>
                                setShowInvalidInvoices(!showInvalidInvoices)
                              }
                              className="group w-full text-left transition-opacity hover:opacity-80 focus:outline-none"
                            >
                              <div className="flex items-center justify-between">
                                <p className="text-brick-primary font-semibold group-hover:underline">
                                  {invalidInvoices.length}{" "}
                                  {invalidInvoices.length === 1
                                    ? "factuur kan"
                                    : "facturen kunnen"}{" "}
                                  niet worden geïmporteerd - klik om te bekijken
                                </p>
                                <svg
                                  className={`text-brick-primary h-5 w-5 transition-transform duration-200 ${showInvalidInvoices ? "rotate-180" : ""}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </button>

                            {showInvalidInvoices && (
                              <>
                                <p className="mt-2 mb-3 text-sm text-neutral-700">
                                  De volgende facturen missen essentiële
                                  gegevens (leverancier ID of factuur ID) en
                                  moeten worden gecorrigeerd in het
                                  boekhoudsysteem:
                                </p>
                                <div className="overflow-hidden rounded border border-neutral-200 bg-white">
                                  <table className="w-full text-sm">
                                    <thead className="border-b border-neutral-200 bg-neutral-50">
                                      <tr>
                                        <th className="p-2 text-left font-medium">
                                          Factuurnummer
                                        </th>
                                        <th className="p-2 text-left font-medium">
                                          Datum
                                        </th>
                                        <th className="p-2 text-left font-medium">
                                          Omschrijving
                                        </th>
                                        <th className="p-2 text-left font-medium">
                                          Bedrag
                                        </th>
                                        <th className="p-2 text-left font-medium">
                                          Probleem
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {invalidInvoices.map((invoice, idx) => (
                                        <tr
                                          key={idx}
                                          className="border-b border-neutral-100 last:border-b-0"
                                        >
                                          <td className="p-2">
                                            {invoice?.move_name || "Onbekend"}
                                          </td>
                                          <td className="p-2">
                                            {invoice?.date
                                              ? format(
                                                  new Date(invoice.date),
                                                  "dd/MM/yyyy",
                                                )
                                              : "Onbekend"}
                                          </td>
                                          <td className="p-2">
                                            {invoice?.name ||
                                              "Geen omschrijving"}
                                          </td>
                                          <td className="p-2">
                                            {invoice?.amount
                                              ? formatCurrency(invoice.amount)
                                              : "Onbekend"}
                                          </td>
                                          <td className="text-brick-primary p-2 font-medium">
                                            {!invoice?.partner_id?.[0] &&
                                              "Leverancier ID ontbreekt"}
                                            {!invoice?.move_line_id?.[0] &&
                                              "Factuur ID ontbreekt"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {validInvoices.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                        <p className="text-lg">
                          Geen geldige facturen gevonden
                        </p>
                        <p className="mt-2 text-sm">
                          Controleer of er facturen beschikbaar zijn voor import
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead className="bg-neutral-50">
                            <tr>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Datum
                              </th>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Factuurnummer
                              </th>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Leverancier in Odoo
                              </th>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Leverancier in Yallyhub
                              </th>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Opmerkingen bij factuur
                              </th>
                              <th className="border-b p-3 text-right text-sm font-medium whitespace-nowrap">
                                Excl. btw
                              </th>
                              <th className="border-b p-3 text-right text-sm font-medium whitespace-nowrap">
                                BTW %
                              </th>
                              <th className="border-b p-3 text-right text-sm font-medium whitespace-nowrap">
                                BTW
                              </th>
                              <th className="border-b p-3 text-right text-sm font-medium whitespace-nowrap">
                                Bedrag incl. btw
                              </th>
                              <th className="border-b p-3 text-left text-sm font-medium whitespace-nowrap">
                                Toewijzing aan project(en)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {validInvoices.map((invoice) => {
                              const moveLineId = invoice.move_line_id[0];
                              const currentSupplierId =
                                supplierAssignments[moveLineId];
                              const assignment = assignments[moveLineId];

                              // Check if this invoice has valid split lines
                              const hasSplitLines =
                                assignment?.splitLines &&
                                assignment.splitLines.length > 0 &&
                                assignment.splitLines.every(
                                  (line) => line.partId > 0,
                                );

                              const hasPart =
                                !!assignment?.partId || hasSplitLines;
                              const hasSupplier = !!currentSupplierId;
                              const vat = resolveInvoiceVat(invoice);

                              return (
                                <tr
                                  key={moveLineId}
                                  className={`${hasSupplier && !hasPart ? "bg-brick-secondary-fine/20" : "hover:bg-neutral-50"} border-b`}
                                >
                                  <td className="p-3 whitespace-nowrap">
                                    {format(
                                      new Date(invoice.date),
                                      "dd/MM/yyyy",
                                    )}
                                  </td>
                                  <td className="min-w-[150px] p-3">
                                    {invoice.move_name}
                                  </td>
                                  <td className="min-w-[150px] p-3">
                                    <span className="font-medium">
                                      {invoice.partner_id[1]}
                                    </span>
                                    <span className="block text-xs text-neutral-400">
                                      ID: {invoice.partner_id[0]}
                                    </span>
                                  </td>
                                  <td className="min-w-[220px] p-3">
                                    <Autocomplete
                                      size="sm"
                                      aria-label="Selecteer leverancier"
                                      label="Selecteer leverancier"
                                      placeholder="Zoek leverancier..."
                                      selectedKey={
                                        currentSupplierId
                                          ? currentSupplierId.toString()
                                          : null
                                      }
                                      onSelectionChange={(key) => {
                                        if (key == null) return;
                                        handleSupplierSelect(moveLineId, {
                                          resourceId: Number(key),
                                        });
                                      }}
                                      popoverProps={{ className: "z-9999" }}
                                    >
                                      {[...getUniqueSuppliers()]
                                        // Surface the Odoo-matched supplier first.
                                        .sort((a, b) => {
                                          const partnerId =
                                            invoice.partner_id[0].toString();
                                          const am =
                                            a.odooId?.toString() === partnerId
                                              ? 0
                                              : 1;
                                          const bm =
                                            b.odooId?.toString() === partnerId
                                              ? 0
                                              : 1;
                                          if (am !== bm) return am - bm;
                                          return (a.name || "").localeCompare(
                                            b.name || "",
                                          );
                                        })
                                        .map((supplier) => {
                                          const isMatch =
                                            supplier.odooId?.toString() ===
                                            invoice.partner_id[0].toString();
                                          const count =
                                            supplier.projectParts.length;
                                          return (
                                            <AutocompleteItem
                                              key={supplier.id.toString()}
                                              textValue={`${supplier.name}${isMatch ? " (match)" : ""}`}
                                            >
                                              <div
                                                className={
                                                  isMatch
                                                    ? "text-moss-primary font-medium"
                                                    : ""
                                                }
                                              >
                                                {supplier.name}
                                                {isMatch && " (match)"}
                                                {count > 0 && (
                                                  <span className="block text-xs text-neutral-400">
                                                    {count === 1
                                                      ? "1 projectonderdeel"
                                                      : `${count} projectonderdelen`}
                                                  </span>
                                                )}
                                              </div>
                                            </AutocompleteItem>
                                          );
                                        })}
                                    </Autocomplete>
                                  </td>
                                  <td className="min-w-[200px] p-3">
                                    <Textarea
                                      placeholder="Voer opmerkingen in"
                                      value={
                                        remarks[moveLineId] || invoice.name
                                      }
                                      onChange={(e) =>
                                        handleRemarksChange(
                                          moveLineId,
                                          e.target.value,
                                        )
                                      }
                                      rows={2}
                                      className="min-h-[36px] resize-none text-sm"
                                    />
                                  </td>
                                  <td className="p-3 text-right whitespace-nowrap">
                                    {formatCurrency(vat.exclVat)}
                                  </td>
                                  <td className="p-3 text-right whitespace-nowrap">
                                    <span
                                      title={vat.percentageTitle}
                                      className={
                                        vat.isUnknown
                                          ? "text-neutral-secondary-mid"
                                          : ""
                                      }
                                    >
                                      {vat.percentageLabel}
                                    </span>
                                    {vat.warning && (
                                      <VatWarningBadge
                                        warning={vat.warning}
                                        iconOnly
                                        className="ml-1 inline-flex align-middle"
                                      />
                                    )}
                                  </td>
                                  <td className="p-3 text-right whitespace-nowrap">
                                    {vat.isUnknown
                                      ? VAT_UNKNOWN
                                      : formatCurrency(vat.vatAmount)}
                                  </td>
                                  <td className="p-3 text-right font-medium whitespace-nowrap">
                                    {formatCurrency(vat.inclVat)}
                                    {vat.warning && (
                                      <VatWarningBadge
                                        warning={vat.warning}
                                        className="mt-1 block"
                                      />
                                    )}
                                  </td>
                                  <td className="min-w-[260px] p-3">
                                    {(() => {
                                      const moveLineId =
                                        invoice.move_line_id[0];
                                      const assignment =
                                        assignments[moveLineId];
                                      const hasSupplier =
                                        !!supplierAssignments[moveLineId];

                                      if (!hasSupplier) {
                                        return (
                                          <div className="text-sm text-neutral-500">
                                            Selecteer eerst een leverancier
                                          </div>
                                        );
                                      }

                                      // Normalise the assignment to a list of
                                      // {projectId, partId, amount} lines — either
                                      // the multi-project split lines, or the single
                                      // auto-matched project/part.
                                      const lines =
                                        assignment?.splitLines &&
                                        assignment.splitLines.length > 0
                                          ? assignment.splitLines
                                          : assignment?.projectId &&
                                              assignment?.partId
                                            ? [
                                                {
                                                  projectId:
                                                    assignment.projectId,
                                                  partId: assignment.partId,
                                                  amount: assignment.amount,
                                                },
                                              ]
                                            : [];

                                      const isAssigned =
                                        lines.length > 0 &&
                                        lines.every(
                                          (l) =>
                                            l.projectId > 0 && l.partId > 0,
                                        );

                                      if (!isAssigned) {
                                        return (
                                          <Button
                                            size="sm"
                                            className="bg-moss-primary w-full text-white"
                                            onPress={() =>
                                              handleOpenSplitModal(invoice)
                                            }
                                            startContent={
                                              <SplitHorizontal size={16} />
                                            }
                                          >
                                            Verdeel over projecten/onderdelen
                                          </Button>
                                        );
                                      }

                                      const projectCount = new Set(
                                        lines.map((l) => l.projectId),
                                      ).size;

                                      return (
                                        <div className="flex items-start gap-2">
                                          <div className="flex flex-1 flex-col gap-0.5">
                                            {lines.map((line, i) => {
                                              const part =
                                                getAvailablePartsForInvoice(
                                                  invoice,
                                                  line.projectId,
                                                ).find(
                                                  (p: any) =>
                                                    p.id === line.partId,
                                                );
                                              return (
                                                <span
                                                  key={i}
                                                  className="text-moss-primary text-sm"
                                                >
                                                  RENO-{line.projectId}
                                                  {part?.title
                                                    ? ` · ${part.title}`
                                                    : ""}
                                                  :{" "}
                                                  <span className="font-medium">
                                                    {formatCurrency(
                                                      Math.abs(line.amount),
                                                    )}
                                                  </span>
                                                </span>
                                              );
                                            })}
                                            {projectCount > 1 && (
                                              <span className="text-xs text-neutral-400">
                                                Verdeeld over {projectCount}{" "}
                                                projecten
                                              </span>
                                            )}
                                          </div>
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            aria-label="Verdeling aanpassen"
                                            className="bg-moss-primary shrink-0 text-white"
                                            onPress={() =>
                                              handleOpenSplitModal(invoice)
                                            }
                                          >
                                            <SplitHorizontal size={16} />
                                          </Button>
                                        </div>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()
            )}
            {getUserSelectedMoveLineIds().length > 0 &&
              !allSelectedInvoicesAssigned() && (
                <div className="bg-brick-secondary-fine border-brick-primary text-neutral-primary mt-4 flex items-start gap-2 rounded-md border p-3">
                  <Info
                    size={20}
                    className="text-brick-primary mt-0.5 shrink-0"
                  />
                  <div>
                    <p className="font-medium">
                      Niet alle facturen zijn volledig toegewezen
                    </p>
                    <p className="text-sm">
                      Verdeel elke geselecteerde factuur over minstens één
                      project en onderdeel via "Verdeel over
                      projecten/onderdelen".
                    </p>
                  </div>
                </div>
              )}
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={handleClose}>
              Annuleren
            </Button>
            <Button
              color="primary"
              onPress={handleImport}
              isDisabled={
                isImporting ||
                getAssignedInvoiceCount() === 0 ||
                (getUserSelectedMoveLineIds().length > 0 &&
                  !allSelectedInvoicesAssigned())
              }
            >
              Importeren ({getAssignedInvoiceCount()})
              {isImporting && (
                <span className="ml-2">
                  <svg
                    className="h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 3.014 1.173 1.656 3.343A9.967 9.967 0 004 12H0c0 3.042 1.135 5.824 3.146 7.935l3.405-2.574a4.24 4.24 0 01-.97-2.217L4.411 3.5h.53a7.46 7.46 0 017.49-7.49v0c3.042 0 5.824 1.135 7.935 3.146A9.967 9.967 0 0024 12h-4z"
                    ></path>
                  </svg>
                </span>
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {currentInvoice &&
        (() => {
          const mlId = currentInvoice.move_line_id[0];
          const supplierId = supplierAssignments[mlId];
          // Only offer projects where the chosen supplier actually has parts.
          const supplierProjects = availableProjects.filter((project) =>
            project.parts.some((part) =>
              part.supplierAssignments?.some(
                (sa) => sa.supplier.id === supplierId,
              ),
            ),
          );
          const defaultProjectId =
            selectedProjects[mlId] || supplierProjects[0]?.id || 0;
          return (
            <SplitInvoiceModal
              isOpen={splitModalOpen}
              onClose={() => setSplitModalOpen(false)}
              invoice={currentInvoice}
              selectedProjectId={defaultProjectId}
              availableProjects={supplierProjects}
              getPartsForProject={(projectId) =>
                getAvailablePartsForInvoice(currentInvoice, projectId)
              }
              onSave={(splitLines) => handleSaveSplit(mlId, splitLines)}
            />
          );
        })()}
    </>
  );
};
